import { TaskResult } from './TaskResult';
import { stateWords } from '@/ui/words';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { failureOf } from '@/api/errors';
import { useRevokeTaskTool, useTwinTask, useTwinTasks } from '@/api/hooks';
import type { WireSourceDetail, WireTaskCapability, WireTaskEvidence, WireTwinTask, WireTwinTaskRow } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { PageHeader } from '@/app/frame';
import { Badge, Button, Kicker, Skeleton } from '@/ui/primitives';
import { RecordHeader, Table, Timeline, when, type TimelineEntry } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { TwinTrailButton } from '@/ui/TwinTrail';
import { ToolLabel, useToolNames } from '@/app/technical';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainTwinTask, explainTwinTaskRow } from './explain';
import { RemoveDialog } from './Remove';
import { CAPABILITY_STATE, GRANT_BASIS, sourceLabel, taskState } from './copy';

/**
 * A person's twin's tasks (Amendment 0011, ADR-0139): theirs alone, as the
 * database shows them (§16). Each is what they confirmed, what it waits on
 * and why, what it did step by step, and what it found — every source
 * marked by whether its page was read back or only a search excerpt said so.
 */
export function TwinTasks({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const names = useToolNames();
  const who = useWho();
  const q = useTwinTasks();
  return (
    <div className={embedded ? 'flex flex-col gap-5' : 'mx-auto flex w-full max-w-[1040px] flex-col gap-5 p-6 phone:p-4'}>
      {!embedded && <PageHeader kicker="Mine" title="Your twin’s tasks" />}
      {q.isPending && <Skeleton rows={5} />}
      {q.isError && <Refusal failure={failureOf(q.error)} />}
      {q.data && (
        <Table<WireTwinTaskRow>
          title="tasks"
          blurb="What you asked your twin to do in the side chat and confirmed. It runs only the plan you read, and waits when a tool needs a superadmin."
          rows={q.data.tasks}
          onOpen={(t) => void navigate(`/tasks/${t.id}`)}
          explain={(t) => explainTwinTaskRow(t, who, names)}
          testId="twin-tasks"
          ask={false}
          emptyTitle="No tasks yet"
          emptyBody="In the side chat, type what you need and press Plan a task. Your twin shows you the plan before anything runs."
          columns={[
            { label: 'Task', get: (t) => t.title, primary: true },
            { label: 'State', get: (t) => taskState(t.state, t.withYou).label, chip: (t) => taskState(t.state, t.withYou) },
            { label: 'Waiting on', get: (t) => (t.waitingOn.length > 0 ? names(t.waitingOn) : '—') },
            { label: 'Asked', get: (t) => when(t.createdAt) },
          ]}
          facets={[{ label: 'State', get: (t) => taskState(t.state, t.withYou).label }]}
        />
      )}
    </div>
  );
}

export function TwinTaskPage({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const taskId = recordId ?? params.taskId ?? '';
  const departmentLabel = useDepartmentLabel();
  const who = useWho();
  const names = useToolNames();
  const q = useTwinTask(taskId);
  const [removing, setRemoving] = useState(false);
  if (q.isPending) return <Skeleton rows={10} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const task = q.data.task;
  const result = [...task.evidence].reverse().find((e) => e.kind === 'result');
  const waiting = task.state === 'waiting' ? [...task.evidence].reverse().find((e) => e.kind === 'waiting') : undefined;
  // Taken off the work with why (ADR-0158), by the person it works for; not
  // while the twin is running it, and not once it has ended.
  const removable = task.state === 'open' || task.state === 'waiting';
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4" data-testid="twin-task">
      {removing && <RemoveDialog target={{ taskId: task.id, twin: true }} title={task.title} onClose={() => setRemoving(false)} />}
      <RecordHeader
        kicker="Your twin’s task"
        title={task.title}
        subtitle={task.plan?.subject ?? null}
        meta={`Asked ${when(task.createdAt)} · ${departmentLabel(task.department)}${task.completedAt ? ` · done ${when(task.completedAt)}` : ''}`}
        chips={[taskState(task.state, task.withYou)]}
        back={{ to: '/tasks', label: 'Your twin’s tasks' }}
        ask={false}
        actions={
          <>
            <TwinTrailButton context={{ taskId: task.id }} where="this task" />
            {removable && <Button icon="trash" onClick={() => setRemoving(true)}>Remove</Button>}
          </>
        }
      />
      {/* What it was asked, where its plan stands and what the person must do (ADR-0155). */}
      <Explainer startOpen label="About this task" context={explainTwinTask(task, who, names)} />
      {waiting?.detail && <Waiting detail={waiting.detail} />}
      {result && <TaskResult evidence={result} />}
      <Capabilities task={task} />
      {task.plan && <PlanRead plan={task.plan} />}
      <Timeline title="What it did" entries={task.evidence.map(entryOf)} note="Every step is kept with the task. A search excerpt is what the search said; a verified source is a page the task read back and found saying it." />
    </div>
  );
}

function Waiting({ detail }: { detail: Record<string, unknown> }) {
  const why = typeof detail['why'] === 'string' ? detail['why'] : null;
  const remedy = typeof detail['remedy'] === 'string' ? detail['remedy'] : null;
  return (
    <section className="flex flex-col gap-1 border-l-2 border-warning bg-raised px-4 py-3" aria-label="Why it waits" data-testid="twin-task-waiting">
      <Kicker>Waiting</Kicker>
      {why && <p className="m-0 text-dense">{why}</p>}
      {remedy && <p className="m-0 text-dense text-secondary">{remedy}</p>}
    </section>
  );
}

/** A link out, only to the web: a source's address came from a search, and nothing else is followed. */
function SourceLink({ url, title }: { url: string; title: string }) {
  if (!/^https?:\/\//i.test(url)) return <span className="[overflow-wrap:anywhere]">{title || url}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer noopener" className="text-accent underline underline-offset-2 [overflow-wrap:anywhere]">
      {title || url} ↗
    </a>
  );
}

function Capabilities({ task }: { task: WireTwinTask }) {
  const me = useMeOrThrow();
  const names = useToolNames();
  const revoke = useRevokeTaskTool();
  const toast = useToast();
  return (
    <section className="flex flex-col gap-2" aria-label="Tools">
      <Kicker>The tools it uses</Kicker>
      <ul className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
        {task.capabilities.map((c: WireTaskCapability) => (
          <li key={c.toolId} className="flex flex-col gap-1 bg-page px-4 py-3 text-dense" data-testid="twin-task-capability">
            <span className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold [overflow-wrap:anywhere]"><ToolLabel id={c.toolId} /></span>
              <Badge tone={CAPABILITY_STATE[c.state].tone}>{CAPABILITY_STATE[c.state].label}</Badge>
              {c.need === 'found_while_running' && <span className="text-meta">found while running</span>}
            </span>
            {c.remedy && <span className="text-secondary">{c.remedy}</span>}
            {c.grant && (
              <span className="flex flex-wrap items-center gap-2 text-meta">
                {GRANT_BASIS[c.grant.basis]}, until {when(c.grant.validTo)}
                {/* A superadmin reading their own task may take back what was granted: narrowing spends no token. */}
                {me.isSuperadmin && new Date(c.grant.validTo) > new Date() && (
                  <Button icon="stop"
                    size="row"
                    variant="quiet"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate({ grantId: c.grant!.id }, { onSuccess: () => toast({ text: `${names([c.toolId])} is taken back from this task.` }) })}
                  >Revoke</Button>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
      {revoke.isError && <Refusal compact failure={failureOf(revoke.error)} />}
    </section>
  );
}

function PlanRead({ plan }: { plan: NonNullable<WireTwinTask['plan']> }) {
  return (
    <section className="flex flex-col gap-2 rounded-brand border border-line p-5" aria-label="The plan you confirmed">
      <Kicker>The plan you confirmed</Kicker>
      <p className="m-0 text-dense"><span className="font-semibold">You get back: </span>{plan.output}</p>
      {plan.queries.length > 0 ? (
        <>
          <span className="text-dense font-semibold">These exact words went to the public web search:</span>
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {plan.queries.map((q) => (
              <li key={q} className="border border-control bg-raised px-2.5 py-1.5 font-mono text-dense [overflow-wrap:anywhere]">{q}</li>
            ))}
          </ol>
        </>
      ) : (
        <span className="text-dense text-secondary">No web search.</span>
      )}
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-dense">
        {plan.steps.map((s) => (
          <li key={s.toolId}>
            <span className="font-semibold"><ToolLabel id={s.toolId} /></span> — <span className="text-secondary">{s.purpose}</span>
          </li>
        ))}
      </ul>
      <span className="text-dense text-meta">At most {plan.maxCalls} tool calls in all.</span>
    </section>
  );
}

const OUTCOME_TONE = { ok: 'accent', refused: 'danger', failed: 'danger', skipped: 'neutral' } as const;

function entryOf(e: WireTaskEvidence): TimelineEntry {
  const base = { id: String(e.seq), when: when(e.at) };
  const erased = <span className="text-meta">Erased with the key it was sealed under.</span>;
  switch (e.kind) {
    case 'tool_call':
      return {
        ...base,
        title: e.toolId ? <ToolLabel id={e.toolId} /> : 'A tool',
        ...(e.outcome ? { chip: { label: stateWords(e.outcome).toUpperCase(), tone: OUTCOME_TONE[e.outcome] } } : {}),
      };
    case 'source': {
      const d = e.detail as WireSourceDetail | null;
      return {
        ...base,
        title: d ? <SourceLink url={d.url} title={d.title} /> : 'A source',
        chip: { label: sourceLabel(e.verified), tone: e.verified ? 'accent' : 'auto' },
        body: d ? (
          <span className="flex flex-col gap-1">
            <span>“{d.excerpt}”</span>
            {d.query && <span className="text-meta">Found by the search <code className="font-mono">{d.query}</code></span>}
          </span>
        ) : (
          erased
        ),
      };
    }
    case 'result':
      return { ...base, title: 'The answer was written', current: true };
    case 'waiting': {
      const why = typeof e.detail?.['why'] === 'string' ? e.detail['why'] : null;
      const remedy = typeof e.detail?.['remedy'] === 'string' ? e.detail['remedy'] : null;
      return {
        ...base,
        title: 'Waited',
        chip: { label: 'WAITING', tone: 'warning' },
        body: e.detail ? <span>{[why, remedy].filter(Boolean).join(' ')}</span> : erased,
      };
    }
  }
}
